/*!
 * Lumia — collection.js (COLLECTION)
 * Vanilla ES2019. Radi na svakom [data-lh-collection] korenu (kolekcija i pretraga).
 *
 *  - sortiranje ([data-sort-select]) i filteri (form[data-facets-form], promene sa debounce-om)
 *    → novi URL (history.pushState) → Lumia.fetchSection(sectionId, url) → zamena regiona
 *    [data-collection-swap="toolbar|grid|facets|facets-footer"] uz čuvanje fokusa, skrola fioke i
 *    otvorenih grupa filtera; #ProductGridContainer ostaje na mestu.
 *  - uklanjanje aktivnih filtera / „Obriši sve“ ([data-facet-remove]) i linkovi filtera po tagovima
 *    ([data-facet-link], putanja /collections/x/tag1+tag2) — isti tok.
 *  - „Prikaži još“ ([data-load-more][data-url]) → fetch sledeće strane + dodavanje kartica u [data-product-grid].
 *  - nazad/napred (popstate) ponovo renderuje prema window.location (i pri promeni tag putanje unutar iste
 *    kolekcije — koren nosi data-collection-url sa čistim URL-om kolekcije); drugačija putanja → reload.
 *  - „Primeni“ u fioci ([data-facets-apply]) samo zatvara fioku (rezultati su već osveženi).
 * Bez JS-a sve forme rade klasičnim GET-om.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var ROOT_SELECTOR = '[data-lh-collection]';
  var CACHE_LIMIT = 24;
  var cache = {};
  var cacheKeys = [];

  function cacheSet(key, html) {
    if (!Object.prototype.hasOwnProperty.call(cache, key)) {
      cacheKeys.push(key);
      if (cacheKeys.length > CACHE_LIMIT) delete cache[cacheKeys.shift()];
    }
    cache[key] = html;
  }

  function toURL(input) {
    try {
      return new URL(String(input), window.location.href);
    } catch (e) {
      return null;
    }
  }

  function keyOf(url) {
    return url.pathname + url.search;
  }

  function isModifiedClick(e) {
    return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (typeof e.button === 'number' && e.button !== 0);
  }

  function firstFocusable(container) {
    if (!container) return null;
    return container.querySelector(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function safeFocus(el, preventScroll) {
    if (!el || typeof el.focus !== 'function') return false;
    try {
      el.focus({ preventScroll: !!preventScroll });
    } catch (e) {
      el.focus();
    }
    return document.activeElement === el;
  }

  /* ------------------------------------------------------------------ */
  /* Controller                                                          */
  /* ------------------------------------------------------------------ */
  function CollectionController(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.drawerId = root.getAttribute('data-facets-drawer') || 'facets-drawer';
    this.updatedText = root.getAttribute('data-updated-text') || '';
    this.currentKey = window.location.pathname + window.location.search;
    this.reqId = 0;
    this.pendingKey = null;
    this.pendingTimer = null;
    this.bind();

    try {
      var state = window.history.state;
      if (!state || !state.lhCollection) {
        window.history.replaceState({ lhCollection: this.currentKey }, '', window.location.href);
      }
    } catch (e) {
      /* history nedostupna (sandbox) — ignoriši */
    }
  }

  CollectionController.prototype.bind = function () {
    var self = this;
    var root = this.root;

    root.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.nodeType !== 1) return;
      if (t.matches('[data-sort-select]')) {
        self.onSort(t);
        return;
      }
      if (t.closest('[data-facets-form]') && t.type !== 'number') {
        self.scheduleFacets(150);
      }
    });

    root.addEventListener('input', function (e) {
      var t = e.target;
      if (!t || t.nodeType !== 1) return;
      if (t.type === 'number' && t.closest('[data-facets-form]')) {
        self.scheduleFacets(650);
      }
    });

    root.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.nodeType !== 1) return;
      if (form.matches('[data-facets-form]') || form.matches('[data-sort-form]')) {
        e.preventDefault();
        self.clearPending();
        self.navigate(self.urlFromForm(form));
      }
    });

    root.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || t.nodeType !== 1) return;
      var el;

      if ((el = t.closest('[data-facet-remove], [data-facet-link]'))) {
        if (isModifiedClick(e)) return;
        if (el.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        self.clearPending();
        self.navigate(el.getAttribute('href'), {
          focusId: el.id || '',
          focusFallback: el.closest('lh-drawer') ? '[data-facets-form]' : '[data-drawer-open="' + self.drawerId + '"]'
        });
        return;
      }

      if ((el = t.closest('[data-load-more]'))) {
        e.preventDefault();
        self.loadMore(el);
        return;
      }

      if ((el = t.closest('[data-facets-apply]'))) {
        e.preventDefault();
        self.flushPending();
        if (window.Lumia && Lumia.drawer) Lumia.drawer.close(self.drawerId);
      }
    });
  };

  /* --- URL helpers ---------------------------------------------------- */
  CollectionController.prototype.urlFromForm = function (form) {
    var url = toURL(form.getAttribute('action') || window.location.pathname);
    if (!url) return null;
    var params = new URLSearchParams();
    var fd = new FormData(form);
    fd.forEach(function (value, name) {
      if (typeof value === 'string' && value.trim() !== '') params.append(name, value.trim());
    });
    url.search = params.toString();
    url.hash = '';
    return url;
  };

  CollectionController.prototype.onSort = function (select) {
    var url = toURL(this.pendingKey || this.currentKey);
    if (!url) return;
    url.searchParams.set('sort_by', select.value);
    var hidden = this.root.querySelector('[data-facets-sort]');
    if (hidden) hidden.value = select.value;
    this.navigate(url, { focusId: select.id });
  };

  CollectionController.prototype.scheduleFacets = function (delay) {
    var self = this;
    this.clearPending();
    this.pendingTimer = window.setTimeout(function () {
      self.pendingTimer = null;
      var form = self.root.querySelector('[data-facets-form]');
      if (form) self.navigate(self.urlFromForm(form));
    }, delay);
  };

  CollectionController.prototype.clearPending = function () {
    if (this.pendingTimer) {
      window.clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
  };

  CollectionController.prototype.flushPending = function () {
    if (!this.pendingTimer) return;
    this.clearPending();
    var form = this.root.querySelector('[data-facets-form]');
    if (form) this.navigate(this.urlFromForm(form));
  };

  CollectionController.prototype.navigate = function (url, opts) {
    var u = typeof url === 'string' ? toURL(url) : url;
    if (!u) return;
    opts = opts || {};
    if (u.origin !== window.location.origin) {
      window.location.href = u.href;
      return;
    }
    if (!opts.keepPage) u.searchParams.delete('page');
    var key = keyOf(u);
    if (!opts.force) {
      if (key === this.pendingKey) return; // isti zahtev je već u toku
      if (key === this.currentKey) {
        if (this.pendingKey !== null) {
          // korisnik se vratio na trenutno stanje dok je zahtev u toku: odbaci taj odgovor
          this.reqId++;
          this.pendingKey = null;
          this.setBusy(false);
          // zahtev iz popstate-a je već promenio URL — vrati ga na stanje koje DOM prikazuje
          if (window.location.pathname + window.location.search !== this.currentKey) {
            try {
              window.history.pushState({ lhCollection: this.currentKey }, '', this.currentKey);
            } catch (e) {
              /* ignoriši */
            }
          }
        }
        return; // DOM već prikazuje stanje koje je korisnik izabrao
      }
    }
    this.render(key, { push: true, focusId: opts.focusId, focusFallback: opts.focusFallback });
  };

  /* --- Section re-render ---------------------------------------------- */
  CollectionController.prototype.render = function (key, opts) {
    var self = this;
    var root = this.root;
    opts = opts || {};
    if (!window.Lumia || typeof Lumia.fetchSection !== 'function') {
      window.location.assign(key);
      return;
    }

    var id = ++this.reqId;
    this.pendingKey = key;
    var active = document.activeElement;
    var activeId = opts.focusId || (active && root.contains(active) ? active.id : '');
    var drawerBody = root.querySelector('[data-facets-container]');
    var drawerScroll = drawerBody ? drawerBody.scrollTop : 0;
    var openState = this.facetOpenState();

    this.setBusy(true);

    var promise = Object.prototype.hasOwnProperty.call(cache, key)
      ? Promise.resolve(cache[key])
      : Lumia.fetchSection(this.sectionId, key).then(function (html) {
          cacheSet(key, html);
          return html;
        });

    promise
      .then(function (html) {
        if (id !== self.reqId) return;
        self.pendingKey = null;
        var doc = Lumia.parseHTML(html);
        var swapped = self.swap(doc);
        if (!swapped) {
          window.location.assign(key);
          return;
        }
        self.applyFacetOpenState(openState);
        self.currentKey = key;
        if (opts.push) {
          try {
            window.history.pushState({ lhCollection: key }, '', key);
          } catch (e) {
            /* ignoriši */
          }
        }
        self.setBusy(false);
        var body = root.querySelector('[data-facets-container]');
        if (body) body.scrollTop = drawerScroll;
        self.restoreFocus(activeId, opts.focusFallback);
        self.announce();
        document.dispatchEvent(
          new CustomEvent('collection:updated', { detail: { sectionId: self.sectionId, url: key, root: root } })
        );
      })
      .catch(function () {
        if (id !== self.reqId) return;
        self.pendingKey = null;
        self.setBusy(false);
        window.location.assign(key);
      });
  };

  CollectionController.prototype.swap = function (doc) {
    var self = this;
    var root = this.root;
    var regions = root.querySelectorAll('[data-collection-swap]');
    var swapped = false;
    var missing = false;
    Array.prototype.forEach.call(regions, function (region) {
      var name = region.getAttribute('data-collection-swap');
      var fresh = doc.querySelector('[data-collection-swap="' + name + '"]');
      if (!fresh) {
        // region postoji lokalno, a u odgovoru ga nema: samo je grid obavezan (bez njega → reload);
        // ostale regione (npr. fioku filtera na pretrazi bez filtera) isprazni umesto da ostanu zastareli
        if (name === 'grid') {
          missing = true;
          return;
        }
        region.innerHTML = '';
        region.removeAttribute('data-total');
        if (name === 'facets' && window.Lumia && Lumia.drawer) Lumia.drawer.close(self.drawerId);
        return;
      }
      region.innerHTML = fresh.innerHTML;
      region.className = fresh.className;
      var total = fresh.getAttribute('data-total');
      if (total !== null) region.setAttribute('data-total', total);
      swapped = true;
    });
    return swapped && !missing;
  };

  CollectionController.prototype.facetOpenState = function () {
    var state = {};
    var groups = this.root.querySelectorAll('[data-facets-container] details[data-facet-key]');
    Array.prototype.forEach.call(groups, function (d) {
      state[d.getAttribute('data-facet-key')] = d.open;
    });
    return state;
  };

  CollectionController.prototype.applyFacetOpenState = function (state) {
    var groups = this.root.querySelectorAll('[data-facets-container] details[data-facet-key]');
    Array.prototype.forEach.call(groups, function (d) {
      var k = d.getAttribute('data-facet-key');
      if (Object.prototype.hasOwnProperty.call(state, k)) d.open = state[k];
    });
  };

  CollectionController.prototype.setBusy = function (busy) {
    var grid = this.root.querySelector('[data-collection-swap="grid"]');
    if (grid) {
      grid.classList.toggle('is-loading', !!busy);
      if (busy) grid.setAttribute('aria-busy', 'true');
      else grid.removeAttribute('aria-busy');
    }
    this.root.classList.toggle('is-updating', !!busy);
  };

  CollectionController.prototype.restoreFocus = function (activeId, fallbackSelector) {
    var root = this.root;
    if (activeId) {
      var el = document.getElementById(activeId);
      if (el && root.contains(el) && safeFocus(el, true)) return;
    }
    if (fallbackSelector) {
      var target = root.querySelector(fallbackSelector);
      if (target) {
        var focusable = target.matches('a, button, input, select, textarea') ? target : firstFocusable(target);
        if (focusable && safeFocus(focusable, true)) return;
      }
    }
    var drawer = document.getElementById(this.drawerId);
    if (drawer && drawer.classList.contains('is-open')) {
      var inDrawer = firstFocusable(drawer.querySelector('[data-facets-form]')) || drawer.querySelector('.lh-drawer__close');
      safeFocus(inDrawer, true);
    }
  };

  CollectionController.prototype.announce = function () {
    var live = this.root.querySelector('[data-collection-live]');
    if (!live) return;
    var count = this.root.querySelector('[data-collection-count]');
    var text = count ? count.textContent.trim() : this.updatedText;
    if (!text) text = this.updatedText;
    live.textContent = '';
    window.setTimeout(function () {
      live.textContent = text;
    }, 50);
  };

  /* --- Prikaži još ---------------------------------------------------- */
  CollectionController.prototype.loadMore = function (btn) {
    var self = this;
    var root = this.root;
    if (btn.classList.contains('is-loading')) return;
    var url = btn.getAttribute('data-url');
    if (!url) return;
    if (!window.Lumia || typeof Lumia.fetchSection !== 'function') {
      window.location.assign(url);
      return;
    }

    var loadingText = btn.getAttribute('data-loading-text');
    btn.classList.add('is-loading');
    btn.setAttribute('aria-disabled', 'true');
    if (loadingText) btn.setAttribute('aria-label', loadingText);

    Lumia.fetchSection(this.sectionId, url)
      .then(function (html) {
        var doc = Lumia.parseHTML(html);
        var freshGrid = doc.querySelector('[data-product-grid]');
        var grid = root.querySelector('[data-product-grid]');
        if (!freshGrid || !grid) {
          window.location.assign(url);
          return;
        }

        var frag = document.createDocumentFragment();
        var firstNew = null;
        Array.prototype.forEach.call(freshGrid.children, function (child) {
          var node = document.importNode(child, true);
          if (!firstNew) firstNew = node;
          frag.appendChild(node);
        });
        grid.appendChild(frag);

        var wrap = root.querySelector('[data-load-more-wrap]');
        var freshWrap = doc.querySelector('[data-load-more-wrap]');
        if (wrap) {
          if (freshWrap) wrap.parentNode.replaceChild(document.importNode(freshWrap, true), wrap);
          else wrap.parentNode.removeChild(wrap);
        }

        var freshUrl = toURL(url);
        if (freshUrl) {
          try {
            window.history.replaceState({ lhCollection: self.currentKey, lhPage: freshUrl.searchParams.get('page') }, '', window.location.href);
          } catch (e) {
            /* ignoriši */
          }
        }

        if (firstNew) {
          var focusable = firstFocusable(firstNew);
          if (focusable) safeFocus(focusable, false);
        }
        self.announce();
        document.dispatchEvent(
          new CustomEvent('collection:appended', { detail: { sectionId: self.sectionId, url: url, root: root, grid: grid } })
        );
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-disabled');
        if (loadingText) btn.removeAttribute('aria-label');
        if (window.Lumia && typeof Lumia.toast === 'function') {
          Lumia.toast((Lumia.strings && Lumia.strings.error) || 'Error', { type: 'error' });
        }
      });
  };

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */
  function init() {
    if (!window.Lumia) return;
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.lhCollection) return;
      if (!root.getAttribute('data-section-id')) return;
      root.lhCollection = new CollectionController(root);
    });
  }

  function activeController() {
    var root = document.querySelector(ROOT_SELECTOR);
    return root && root.lhCollection ? root.lhCollection : null;
  }

  if (!window.__lhCollectionBound) {
    window.__lhCollectionBound = true;

    window.addEventListener('popstate', function () {
      var ctrl = activeController();
      if (!ctrl) return;
      var key = window.location.pathname + window.location.search;
      if (key === ctrl.currentKey) return;
      var current = toURL(ctrl.currentKey);
      var p = window.location.pathname;
      var samePath = !!current && current.pathname === p;
      // tag putanje (/collections/x/tag1+tag2) pripadaju istoj kolekciji → render bez reload-a
      var base = (ctrl.root.getAttribute('data-collection-url') || '').split('?')[0];
      var sameCollection = !!base && (p === base || (p.indexOf(base + '/') === 0 && p.indexOf(base + '/products/') !== 0));
      if (!samePath && !sameCollection) {
        window.location.reload();
        return;
      }
      ctrl.render(key, { push: false });
    });

    document.addEventListener('shopify:section:load', function () {
      init();
    });
  }

  function boot() {
    if (window.Lumia) {
      init();
    } else {
      document.addEventListener('lumia:ready', init, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
